import { ChevronDown } from 'lucide-react';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { Button } from '@/components/ui/Button';
import { COMPLETION_FIELD_LABELS } from '@/lib/constants/dashboard-strings';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import type { StartupProfileCompletion } from '../../api/startup-profile.api';
import { STARTUP_COMPLETION_SECTION, type StartupSectionId } from './startup-sections';

export interface StartupCompletionMenuProps {
  completion: StartupProfileCompletion;
  /** Opens the side panel of the card that holds a missing field. */
  onOpenSection: (section: StartupSectionId) => void;
}

/**
 * The header's completion % with an "N items left" menu (FR-171, Flow 2).
 * Each item opens the card that fixes it; an item no form can edit is listed
 * but disabled. Hidden entirely at 100%.
 */
export function StartupCompletionMenu({ completion, onOpenSection }: StartupCompletionMenuProps) {
  const missing = completion.missingFields;
  const items = missing.map((field) => {
    const section = STARTUP_COMPLETION_SECTION[field] ?? null;
    return {
      label: COMPLETION_FIELD_LABELS[field] ?? field,
      disabled: section === null,
      onSelect: () => {
        if (section) onOpenSection(section);
      },
    };
  });

  return (
    <div className="text-p5 mt-1.5 flex flex-col gap-1 font-normal">
      <div className="flex items-center gap-2.5">
        <span
          role="progressbar"
          aria-label={L.PROFILE_COMPLETE}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={completion.pct}
          className="h-2 flex-1 overflow-hidden rounded-full bg-white/12"
        >
          <span
            className="bg-green block h-full rounded-full"
            style={{ width: `${completion.pct}%` }}
          />
        </span>
        <span className="font-semibold">{completion.pct}%</span>
      </div>
      {missing.length > 0 && (
        <DropdownMenu
          trigger={
            <Button
              variant="link-on-dark"
              className="inline-flex items-center gap-1"
            >
              {L.ITEMS_LEFT(missing.length)}
              <ChevronDown size={14} aria-hidden />
            </Button>
          }
          items={items}
        />
      )}
    </div>
  );
}
